/**
 * Modèle géodésique unique du produit — EF-CAL-010.
 *
 * Plan tangent à l'ellipsoïde WGS 84, ancré à la latitude médiane du couple
 * considéré. Aucune autre méthode de calcul de distance n'existe dans le code.
 *
 * L'usage de la latitude médiane rend la distance symétrique au bit près,
 * propriété nécessaire au caractère déterministe de l'ordre du roadbook
 * exigé par CA-023.
 */

import { DEG_TO_RAD, RAD_TO_DEG } from '../core/units';

/** Demi-grand axe WGS 84, en mètres. */
export const WGS84_A = 6378137;
/** Aplatissement WGS 84. */
export const WGS84_F = 1 / 298.257223563;
/** Première excentricité au carré : e² = f (2 − f). */
export const WGS84_E2 = WGS84_F * (2 - WGS84_F);

export interface Coordinate {
  readonly lat: number; // degrés décimaux
  readonly lon: number; // degrés décimaux
}

/** Rayons de courbure méridien et de grande normale à la latitude donnée. */
export interface LocalRadii {
  readonly meridian: number; // M
  readonly normal: number; // N
  readonly cosLat: number; // cos φ₀
}

export function localRadii(latitudeDegrees: number): LocalRadii {
  const phi = latitudeDegrees * DEG_TO_RAD;
  const sinPhi = Math.sin(phi);
  const w = Math.sqrt(1 - WGS84_E2 * sinPhi * sinPhi);
  return {
    meridian: (WGS84_A * (1 - WGS84_E2)) / (w * w * w),
    normal: WGS84_A / w,
    cosLat: Math.cos(phi),
  };
}

/**
 * Différence de longitude normalisée à l'intervalle (−π, π], exprimée en
 * degrés, afin qu'un parcours franchissant l'antiméridien reste correct.
 */
export function normalizeLongitudeDelta(deltaDegrees: number): number {
  let d = deltaDegrees;
  while (d > 180) d -= 360;
  while (d <= -180) d += 360;
  return d;
}

/**
 * Distance entre deux points, en mètres.
 * `d(A, B) === d(B, A)` au bit près.
 */
export function distance(a: Coordinate, b: Coordinate): number {
  const midLat = (a.lat + b.lat) / 2;
  const { meridian, normal, cosLat } = localRadii(midLat);
  const north = meridian * (b.lat - a.lat) * DEG_TO_RAD;
  const east = normal * cosLat * normalizeLongitudeDelta(b.lon - a.lon) * DEG_TO_RAD;
  return Math.hypot(north, east);
}

/** Point exprimé en mètres dans un repère local plan. */
export interface LocalPoint {
  readonly north: number;
  readonly east: number;
}

/** Projette un point dans le repère local ancré en `origin`, aux rayons donnés. */
export function toLocalPoint(point: Coordinate, origin: Coordinate, radii: LocalRadii): LocalPoint {
  return {
    north: radii.meridian * (point.lat - origin.lat) * DEG_TO_RAD,
    east: radii.normal * radii.cosLat * normalizeLongitudeDelta(point.lon - origin.lon) * DEG_TO_RAD,
  };
}

export interface SegmentProjection {
  /** Paramètre borné à [0, 1] le long du segment. */
  readonly t: number;
  /** Distance du point au segment, en mètres. */
  readonly distance: number;
  /** Coordonnée du point projeté. */
  readonly projected: Coordinate;
  /** Contribution à la progression : t × |B−A|, en mètres. */
  readonly along: number;
  /** Longueur du segment, en mètres. */
  readonly segmentLength: number;
}

/**
 * Projection d'un point `p` sur le segment `[a, b]`, dans le repère local du
 * segment, origine en `a`. La borne à [0, 1] traite les cas où la projection
 * tombe hors du segment : le point le plus proche est alors une extrémité.
 */
export function projectOnSegment(p: Coordinate, a: Coordinate, b: Coordinate): SegmentProjection {
  const radii = localRadii((a.lat + b.lat) / 2);
  const pb = toLocalPoint(b, a, radii);
  const pp = toLocalPoint(p, a, radii);
  const lengthSquared = pb.north * pb.north + pb.east * pb.east;
  const segmentLength = Math.sqrt(lengthSquared);

  if (lengthSquared === 0) {
    return {
      t: 0,
      distance: Math.hypot(pp.north, pp.east),
      projected: a,
      along: 0,
      segmentLength: 0,
    };
  }

  const dot = pp.north * pb.north + pp.east * pb.east;
  const t = Math.min(1, Math.max(0, dot / lengthSquared));
  const projectedNorth = t * pb.north;
  const projectedEast = t * pb.east;
  return {
    t,
    distance: Math.hypot(pp.north - projectedNorth, pp.east - projectedEast),
    projected: interpolate(a, b, t),
    along: t * segmentLength,
    segmentLength,
  };
}

/**
 * Interpolation linéaire de la latitude et de la longitude, proportionnelle à
 * la fraction `t`. À un pas de 100 m, l'écart avec une interpolation
 * géodésique est inférieur au micromètre (EF-CAL-010).
 */
export function interpolate(a: Coordinate, b: Coordinate, t: number): Coordinate {
  const deltaLon = normalizeLongitudeDelta(b.lon - a.lon);
  let lon = a.lon + deltaLon * t;
  if (lon > 180) lon -= 360;
  if (lon <= -180) lon += 360;
  return { lat: a.lat + (b.lat - a.lat) * t, lon };
}

/**
 * Cap, ou relèvement par rapport au nord géographique, en degrés dans [0, 360[.
 * Calculé dans le repère local ancré sur la position courante. N'utilise pas
 * le magnétomètre et ne requiert aucune autorisation supplémentaire.
 */
export function bearing(from: Coordinate, to: Coordinate): number {
  const radii = localRadii(from.lat);
  const north = radii.meridian * (to.lat - from.lat) * DEG_TO_RAD;
  const east = radii.normal * radii.cosLat * normalizeLongitudeDelta(to.lon - from.lon) * DEG_TO_RAD;
  const angle = Math.atan2(east, north) * RAD_TO_DEG;
  return ((angle % 360) + 360) % 360;
}

export type Cardinal = 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SO' | 'O' | 'NO';

const CARDINALS: readonly Cardinal[] = ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO'];

/**
 * Point cardinal sur huit directions. Chaque secteur couvre 45° centré sur sa
 * direction, fermé à sa borne inférieure et ouvert à sa borne supérieure.
 */
export function cardinal(bearingDegrees: number): Cardinal {
  const normalized = ((bearingDegrees % 360) + 360) % 360;
  const index = Math.floor((normalized + 22.5) / 45) % 8;
  const value = CARDINALS[index];
  if (value === undefined) throw new Error('Secteur cardinal hors bornes');
  return value;
}

/** Longueur cumulée d'une polyligne, en mètres. */
export function polylineLength(points: readonly Coordinate[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    const previous = points[i - 1];
    const current = points[i];
    if (previous === undefined || current === undefined) continue;
    total += distance(previous, current);
  }
  return total;
}
